import { Helmet } from "react-helmet-async";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import api from "../Utils/Axios";
import DashboardHeader from "./CompanyComponents/DashboardHeaderRouter";
import UrduKeyboard from "../Components/UrduKeyboard";

function UserManage() {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [companyLanguage, setCompanyLanguage] = useState('en');
  const [ledgerParties, setLedgerParties] = useState([]);
  const [activeTab, setActiveTab] = useState('suppliers'); // 'suppliers' or 'customers'
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingParty, setEditingParty] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    nameUrdu: '',
    type: 'supplier',
    phone: '',
    address: '',
    openingBalance: '',
    balanceType: 'receivable',
    notes: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const printRef = useRef(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showUrduKeyboard, setShowUrduKeyboard] = useState(false);
  const [showFormUrduKeyboard, setShowFormUrduKeyboard] = useState(false);
  const searchInputRef = useRef(null);
  const formUrduNameInputRef = useRef(null);

  // Handle Urdu keyboard input for search
  const handleSearchUrduKeyPress = (key) => {
    if (key === 'backspace') {
      setSearchTerm((prev) => prev ? prev.slice(0, -1) : '');
    } else {
      setSearchTerm((prev) => (prev || '') + key);
    }
  };

  // Toggle Urdu keyboard on search input click
  const handleSearchInputClick = () => {
    setShowUrduKeyboard(prev => !prev);
  };

  // Handle Urdu keyboard input for form nameUrdu field
  const handleFormUrduKeyPress = (key) => {
    if (key === 'backspace') {
      setFormData((prev) => ({
        ...prev,
        nameUrdu: prev.nameUrdu ? prev.nameUrdu.slice(0, -1) : ''
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        nameUrdu: (prev.nameUrdu || '') + key
      }));
    }
  };

  // Toggle Urdu keyboard on form Urdu name input click
  const handleFormUrduNameClick = () => {
    setShowFormUrduKeyboard(prev => !prev);
  };

  // Check company login
  useEffect(() => {
    const token = localStorage.getItem("companyToken");
    const savedCompany = localStorage.getItem("companyData");
    const savedLanguage = localStorage.getItem("companyLanguage");

    if (!token || !savedCompany) {
      navigate("/");
      return;
    }

    try {
      const companyData = JSON.parse(savedCompany);
      setCompany(companyData);
      setCompanyLanguage(savedLanguage || 'en');
    } catch (error) {
      console.error("Error parsing company data:", error);
      navigate("/");
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  // Fetch ledger parties
  const fetchLedgerParties = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      const response = await api.get("/ledger/parties", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLedgerParties(response.data.parties || []);
    } catch (error) {
      console.error("Error fetching ledger parties:", error);
      toast.error(companyLanguage === 'ur' ? 'ڈیٹا لوڈ نہیں ہو سکا' : 'Failed to load data');
    }
  };

  useEffect(() => {
    if (company) {
      fetchLedgerParties();
    }
  }, [company]);

  // Filter parties by type and search term (works with both English and Urdu)
  const filteredParties = ledgerParties.filter(party => {
    const matchesType = party.type === activeTab.slice(0, -1); // 'suppliers' -> 'supplier', 'customers' -> 'customer'
    if (!searchTerm) return matchesType;

    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      party.name?.toLowerCase().includes(searchLower) ||
      party.nameUrdu?.toLowerCase().includes(searchLower) ||
      party.nameUrdu?.includes(searchTerm) || // Also check without lowercase for exact Urdu match
      party.phone?.includes(searchTerm) ||
      party.address?.toLowerCase().includes(searchLower);
    return matchesType && matchesSearch;
  });

  // Calculate totals for suppliers and customers
  const suppliersTotal = ledgerParties
    .filter(party => party.type === 'supplier')
    .reduce((sum, party) => sum + parseFloat(party.currentBalance || 0), 0);

  const customersTotal = ledgerParties
    .filter(party => party.type === 'customer')
    .reduce((sum, party) => sum + parseFloat(party.currentBalance || 0), 0);

  // Handle edit
  const handleEdit = (party) => {
    setEditingParty(party);
    setFormData({
      name: party.name || '',
      nameUrdu: party.nameUrdu || '',
      type: party.type || 'supplier',
      phone: party.phone || '',
      address: party.address || '',
      openingBalance: party.openingBalance || '',
      balanceType: party.balanceType || 'receivable',
      notes: party.notes || ''
    });
    setShowEditModal(true);
  };

  // Handle delete
  const handleDelete = async (partyId) => {
    const confirmMsg = companyLanguage === 'ur'
      ? 'کیا آپ واقعی اس پارٹی کو حذف کرنا چاہتے ہیں؟'
      : 'Are you sure you want to delete this party?';

    if (!window.confirm(confirmMsg)) return;

    try {
      const token = localStorage.getItem("companyToken");
      await api.delete(`/ledger/parties/${partyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(companyLanguage === 'ur' ? 'پارٹی حذف ہو گئی' : 'Party deleted successfully');
      fetchLedgerParties();
    } catch (error) {
      console.error("Error deleting party:", error);
      toast.error(companyLanguage === 'ur' ? 'پارٹی حذف نہیں ہو سکی' : 'Failed to delete party');
    }
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("companyToken");
      // Safely parse opening balance (remove non-numeric characters, support decimals)
      const cleanedOpening = String(formData.openingBalance || '')
        .replace(/[^0-9.-]/g, '');
      const openingValue = cleanedOpening ? parseFloat(cleanedOpening) : 0;

      const partyData = {
        ...formData,
        openingBalance: isNaN(openingValue) ? 0 : openingValue
      };

      if (editingParty) {
        // Update
        await api.put(`/ledger/parties/${editingParty._id}`, partyData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(companyLanguage === 'ur' ? 'پارٹی اپ ڈیٹ ہو گئی' : 'Party updated successfully');
      } else {
        // Create
        await api.post("/ledger/parties", partyData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(companyLanguage === 'ur' ? 'پارٹی بنا دی گئی' : 'Party created successfully');
      }

      setShowEditModal(false);
      setEditingParty(null);
      setFormData({
        name: '',
        nameUrdu: '',
        type: activeTab.slice(0, -1),
        phone: '',
        address: '',
        openingBalance: '',
        balanceType: 'receivable',
        notes: ''
      });
      fetchLedgerParties();
    } catch (error) {
      console.error("Error saving party:", error);
      toast.error(companyLanguage === 'ur' ? 'پارٹی محفوظ نہیں ہو سکی' : 'Failed to save party');
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem("companyToken");
    localStorage.removeItem("companyData");
    localStorage.removeItem("companyLanguage");
    toast.info(companyLanguage === 'ur' ? "کامیابی سے لاگ آؤٹ ہو گئے" : "Logged out successfully");
    navigate("/");
  };

  // Handle go back to dashboard based on company type
  const handleGoBack = () => {
    if (company) {
      const dashboardType = company.dashboardType || company.dashboard_type || 'main';
      if (dashboardType === 'part2' || dashboardType === 'dashboard-part2' || dashboardType === 'part-2' || dashboardType === 'ur' || dashboardType === 'urdu') {
        navigate('/company/dashboard-part2');
      } else if (dashboardType === 'en' || dashboardType === 'english') {
        navigate('/company/dashboard-en');
      } else {
        navigate('/company/dashboard');
      }
    } else {
      navigate('/company/dashboard');
    }
  };

  // Download PDF
  const handleDownloadPDF = async () => {
    if (!printRef.current) {
      toast.error(companyLanguage === 'ur' ? 'مواد نہیں ملا' : 'Content not found');
      return;
    }

    try {
      setIsGeneratingPDF(true);
      toast.info(companyLanguage === 'ur' ? 'PDF بنایا جا رہا ہے...' : 'Generating PDF...');

      const element = printRef.current;

      // Show print header and hide other elements for PDF
      const printHeader = element.querySelector('.pdf-print-header');

      // Show print header
      if (printHeader) {
        printHeader.style.display = 'block';
      }

      // Hide elements that should be hidden in print (using attribute selector)
      const hiddenElements = element.querySelectorAll('[class*="print:hidden"]');
      hiddenElements.forEach(el => {
        el.style.display = 'none';
      });

      // Add temporary styles for PDF
      const style = document.createElement('style');
      style.id = 'pdf-print-styles';
      style.textContent = `
        .bg-gradient-to-br { background: white !important; }
        .from-green-50 { background: white !important; }
        .via-emerald-50 { background: white !important; }
        .to-teal-50 { background: white !important; }
        .bg-white { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 0.5cm !important; }
        .rounded-xl { border-radius: 0 !important; }
        .shadow-lg { box-shadow: none !important; }
        table { width: 100% !important; max-width: 100% !important; table-layout: auto !important; }
        .overflow-x-auto { width: 100% !important; overflow: visible !important; }
        div[ref] { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 0 !important; }
      `;
      document.head.appendChild(style);

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight
      });

      // Restore original display states
      if (printHeader) {
        printHeader.style.display = '';
      }
      hiddenElements.forEach(el => {
        el.style.display = '';
      });

      // Remove temporary styles
      const tempStyle = document.getElementById('pdf-print-styles');
      if (tempStyle) {
        document.head.removeChild(tempStyle);
      }

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const tabName = activeTab === 'suppliers' ? (companyLanguage === 'ur' ? 'سپلائرز' : 'Suppliers') : (companyLanguage === 'ur' ? 'کسٹمرز' : 'Customers');
      const fileName = `${tabName}_List_${new Date().toLocaleDateString('en-CA')}.pdf`;

      pdf.save(fileName);
      toast.success(companyLanguage === 'ur' ? 'PDF ڈاؤن لوڈ ہو گیا!' : 'PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error(companyLanguage === 'ur' ? 'PDF بنانے میں ناکام' : 'Failed to generate PDF');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Download Excel
  const handleDownloadExcel = () => {
    try {
      const excelData = [];

      // Add header
      excelData.push([
        companyLanguage === 'ur' ? 'نام' : 'Name',
        companyLanguage === 'ur' ? 'نام (اردو)' : 'Name (Urdu)',
        companyLanguage === 'ur' ? 'فون' : 'Phone',
        companyLanguage === 'ur' ? 'پتہ' : 'Address',
        companyLanguage === 'ur' ? 'بیلنس' : 'Balance',
        companyLanguage === 'ur' ? 'بیلنس کی قسم' : 'Balance Type',
        companyLanguage === 'ur' ? 'نوٹس' : 'Notes'
      ]);

      // Add data rows
      filteredParties.forEach(party => {
        excelData.push([
          party.name || '-',
          party.nameUrdu || '-',
          party.phone || '-',
          party.address || '-',
          parseFloat(party.currentBalance || 0).toLocaleString(),
          party.balanceType === 'receivable' ? (companyLanguage === 'ur' ? 'قابل وصول' : 'Receivable') : (companyLanguage === 'ur' ? 'قابل ادائیگی' : 'Payable'),
          party.notes || '-'
        ]);
      });

      // Add total row
      const totalAmount = activeTab === 'suppliers' ? suppliersTotal : customersTotal;
      excelData.push([
        companyLanguage === 'ur' ? 'کل رقم' : 'Total',
        '',
        '',
        '',
        totalAmount.toLocaleString(),
        '',
        ''
      ]);

      // Create workbook and worksheet
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      const tabName = activeTab === 'suppliers' ? 'Suppliers' : 'Customers';
      XLSX.utils.book_append_sheet(wb, ws, tabName);

      // Generate filename
      const fileName = `${tabName}_List_${new Date().toLocaleDateString('en-CA')}.xlsx`;

      XLSX.writeFile(wb, fileName);
      toast.success(companyLanguage === 'ur' ? 'Excel فائل ڈاؤن لوڈ ہو گئی' : 'Excel file downloaded successfully');
    } catch (error) {
      console.error('Excel download error:', error);
      toast.error(companyLanguage === 'ur' ? 'Excel ڈاؤن لوڈ میں ناکام' : 'Failed to download Excel');
    }
  };

  // Print
  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{companyLanguage === 'ur' ? 'لوڈ ہو رہا ہے...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  const isUrdu = companyLanguage === 'ur';

  return (
    <>
      {/* Company header hidden on UserManage page */}
      {/* {company && (
        <DashboardHeader
          company={company}
          companyLanguage={companyLanguage}
          onLogout={handleLogout}
        />
      )} */}
      <Helmet>
        <title>{isUrdu ? 'یوزر مینجمنٹ' : 'User Management'}</title>
      </Helmet>
      <div className={`min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 pt-[6px] pb-8 px-4 ${isUrdu ? 'font-urdu' : ''}`} dir={isUrdu ? 'rtl' : 'ltr'}>
        <div className="max-w-7xl mx-auto">
          {/* PDF/Print Container */}
          <div ref={printRef}>
            {/* Print Header - Company Info - Top of Page */}
            {company && (
              <div className="hidden print:block border-b-2 border-gray-400 pb-3 mb-4 bg-white p-4 pdf-print-header">
                <div className="flex items-start justify-between mb-2 gap-4">
                  {/* Company Name - Top Left */}
                  <div className="text-left flex-shrink-0">
                    <h1
                      className={`text-xl font-bold ${isUrdu ? 'font-urdu' : ''}`}
                      style={{
                        ...(isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}),
                        color: '#1e40af',
                        WebkitPrintColorAdjust: 'exact',
                        printColorAdjust: 'exact'
                      }}
                    >
                      {company?.shopName || company?.username || company?.shopNameUrdu || 'Company Name'}
                    </h1>
                    {company?.username && (
                      <p className={`text-sm text-gray-600 font-medium mt-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                        {isUrdu ? 'نام:' : 'Name:'} {company.username}
                      </p>
                    )}
                    {(company?.contactNumber || company?.phone || company?.mobile) && (
                      <p className="text-sm text-gray-700 font-semibold mt-1">
                        {isUrdu ? 'فون:' : 'Phone:'} {company.contactNumber || company.phone || company.mobile}
                      </p>
                    )}
                  </div>

                  {/* Logo - Top Right */}
                  <div className="flex-shrink-0" style={{ alignSelf: 'flex-start' }}>
                    {company?.logo && (
                      <img
                        src={company.logo}
                        alt="Logo"
                        className="h-16 w-16 object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                </div>
                {company?.address && (
                  <div className="mt-2 pt-2 border-t border-gray-300">
                    <p className={`text-xs text-gray-700 text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                      {company.address}
                    </p>
                  </div>
                )}
                {/* Tab Title */}
                <div className="mt-3 pt-2">
                  <h2 className={`text-lg font-bold text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                    {activeTab === 'suppliers'
                      ? (isUrdu ? 'سپلائرز' : 'Suppliers')
                      : (isUrdu ? 'کسٹمرز' : 'Customers')}
                  </h2>
                </div>
              </div>
            )}

            {/* Header */}
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border-2 border-green-200 print:hidden">
              <div className="flex justify-between items-center flex-wrap gap-4">
                {isUrdu ? (
                  <>
                    {/* Page Title - Left side in Urdu */}
                    <div className="flex items-center gap-4">
                      <h1 className="text-3xl font-bold text-gray-900 font-urdu">
                        یوزر مینجمنٹ
                      </h1>
                    </div>
                    {/* Action Buttons - Right side in Urdu */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        onClick={() => {
                          setEditingParty(null);
                          setFormData({
                            name: '',
                            nameUrdu: '',
                            type: activeTab.slice(0, -1),
                            phone: '',
                            address: '',
                            openingBalance: '',
                            balanceType: 'receivable',
                            notes: ''
                          });
                          setShowEditModal(true);
                        }}
                        className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold px-6 py-3 rounded-lg transition-all transform hover:scale-105 hover:shadow-lg flex items-center gap-2 print:hidden font-urdu"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        نیا شامل کریں
                      </button>
                      <button
                        onClick={handlePrint}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-all flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                        پرنٹ
                      </button>
                      <button
                        onClick={handleDownloadExcel}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-all flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Excel ڈاؤن لوڈ
                      </button>
                      <button
                        onClick={handleDownloadPDF}
                        disabled={isGeneratingPDF}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-all flex items-center gap-2 disabled:opacity-50"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {isGeneratingPDF ? 'لوڈ ہو رہا ہے...' : 'PDF ڈاؤن لوڈ'}
                      </button>
                      <button
                        onClick={handleGoBack}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-all flex items-center gap-2"
                        title="ڈیش بورڈ پر واپس جائیں"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        ڈیش بورڈ
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Action Buttons - Left side in English */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        onClick={() => {
                          setEditingParty(null);
                          setFormData({
                            name: '',
                            nameUrdu: '',
                            type: activeTab.slice(0, -1),
                            phone: '',
                            address: '',
                            openingBalance: '',
                            balanceType: 'receivable',
                            notes: ''
                          });
                          setShowEditModal(true);
                        }}
                        className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold px-6 py-3 rounded-lg transition-all transform hover:scale-105 hover:shadow-lg flex items-center gap-2 print:hidden"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add New
                      </button>
                      <button
                        onClick={handlePrint}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-all flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                        Print
                      </button>
                      <button
                        onClick={handleDownloadExcel}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-all flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download Excel
                      </button>
                      <button
                        onClick={handleDownloadPDF}
                        disabled={isGeneratingPDF}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-all flex items-center gap-2 disabled:opacity-50"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {isGeneratingPDF ? 'Loading...' : 'Download PDF'}
                      </button>
                      <button
                        onClick={handleGoBack}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-all flex items-center gap-2"
                        title="Back to Dashboard"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Dashboard
                      </button>
                    </div>
                    {/* Page Title - Right side in English */}
                    <div className="flex items-center gap-4">
                      <h1 className="text-3xl font-bold text-gray-900">
                        User Management
                      </h1>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-xl shadow-lg mb-6 overflow-hidden border-2 border-green-200 print:hidden">
              <div className="flex border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('suppliers')}
                  className={`flex-1 px-6 py-4 font-semibold transition-all ${activeTab === 'suppliers'
                    ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    } ${isUrdu ? 'font-urdu' : ''}`}
                >
                  {isUrdu ? `سپلائرز (${ledgerParties.filter(p => p.type === 'supplier').length})` : `Suppliers (${ledgerParties.filter(p => p.type === 'supplier').length})`}
                </button>
                <button
                  onClick={() => setActiveTab('customers')}
                  className={`flex-1 px-6 py-4 font-semibold transition-all ${activeTab === 'customers'
                    ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    } ${isUrdu ? 'font-urdu' : ''}`}
                >
                  {isUrdu ? `کسٹمرز (${ledgerParties.filter(p => p.type === 'customer').length})` : `Customers (${ledgerParties.filter(p => p.type === 'customer').length})`}
                </button>
              </div>
            </div>

            {/* Search Bar */}
            <div className="bg-white rounded-xl shadow-lg p-4 mb-6 border-2 border-green-200 print:hidden">
              <div className="flex items-center gap-4">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={handleSearchInputClick}
                  placeholder={isUrdu ? 'تلاش کریں...' : 'Search...'}
                  className={`flex-1 px-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 ${isUrdu ? 'font-urdu text-right' : ''}`}
                  style={isUrdu ? {
                    fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif",
                    lineHeight: '1.8',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    fontSize: '13px',
                    height: 'auto',
                    minHeight: '44px',
                    display: 'block',
                    overflow: 'visible',
                    verticalAlign: 'middle'
                  } : {}}
                  dir={isUrdu ? 'rtl' : 'ltr'}
                  lang={isUrdu ? 'ur' : 'en'}
                />
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-green-200">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-green-600 to-emerald-600 text-white">
                    <tr>
                      <th className={`px-6 py-4 text-left font-semibold ${isUrdu ? 'font-urdu text-right' : ''}`}>
                        {isUrdu ? 'نام' : 'Name'}
                      </th>
                      <th className={`px-6 py-4 text-left font-semibold ${isUrdu ? 'font-urdu text-right' : ''}`}>
                        {isUrdu ? 'فون' : 'Phone'}
                      </th>
                      <th className={`px-6 py-4 text-left font-semibold ${isUrdu ? 'font-urdu text-right' : ''}`}>
                        {isUrdu ? 'پتہ' : 'Address'}
                      </th>
                      <th className={`px-6 py-4 text-left font-semibold ${isUrdu ? 'font-urdu text-right' : ''}`}>
                        {isUrdu ? 'بیلنس' : 'Balance'}
                      </th>
                      <th className={`px-6 py-4 text-left font-semibold ${isUrdu ? 'font-urdu text-right' : ''}`}>
                        {isUrdu ? 'نوٹس' : 'Notes'}
                      </th>
                      <th className={`px-6 py-4 text-center font-semibold ${isUrdu ? 'font-urdu' : ''}`}>
                        {isUrdu ? 'ایکشن' : 'Actions'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredParties.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <p className={isUrdu ? 'font-urdu' : ''}>
                            {isUrdu ? 'کوئی ڈیٹا نہیں ملا' : 'No data found'}
                          </p>
                        </td>
                      </tr>
                    ) : (
                      filteredParties.map((party) => (
                        <tr key={party._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <div>
                              <div className={`font-semibold text-gray-900 ${isUrdu ? 'font-urdu' : ''}`}>
                                {isUrdu ? (party.nameUrdu || party.name) : (party.name || party.nameUrdu)}
                              </div>
                              {party.nameUrdu && party.name && (
                                <div className="text-sm text-gray-500 mt-1">
                                  {isUrdu ? party.name : party.nameUrdu}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-gray-700">
                            {party.phone || '-'}
                          </td>
                          <td className="px-6 py-4 text-gray-700">
                            <div className={`max-w-xs truncate ${isUrdu ? 'text-right' : 'text-left'}`}>
                              {party.address || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <span className={`font-semibold ${parseFloat(party.currentBalance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {parseFloat(party.currentBalance || 0).toLocaleString()}
                              </span>
                              {parseFloat(party.currentBalance || 0) !== 0 && (
                                <span className="text-xs text-gray-500">
                                  {party.balanceType === 'receivable' ? (isUrdu ? 'قابل وصول' : 'Receivable') : (isUrdu ? 'قابل ادائیگی' : 'Payable')}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className={`max-w-xs truncate text-gray-600 ${isUrdu ? 'text-right' : 'text-left'}`}>
                              {party.notes || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleEdit(party)}
                                className="text-green-600 hover:text-green-800 font-semibold p-2 hover:bg-green-50 rounded transition-all"
                                title={isUrdu ? 'ترمیم' : 'Edit'}
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDelete(party._id)}
                                className="text-red-600 hover:text-red-800 font-semibold p-2 hover:bg-red-50 rounded transition-all"
                                title={isUrdu ? 'حذف' : 'Delete'}
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                    <tr>
                      <td colSpan="3" className={`px-6 py-4 font-bold text-gray-900 ${isUrdu ? 'font-urdu text-right' : 'text-left'}`}>
                        {isUrdu ? 'کل رقم:' : 'Total:'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className={`font-bold text-lg ${(activeTab === 'suppliers' ? suppliersTotal : customersTotal) >= 0
                            ? 'text-green-600'
                            : 'text-red-600'
                            }`}>
                            {(activeTab === 'suppliers' ? suppliersTotal : customersTotal).toLocaleString()}
                          </span>
                        </div>
                      </td>
                      <td colSpan="2" className="px-6 py-4"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit/Create Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir={isUrdu ? 'rtl' : 'ltr'}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-6 rounded-t-xl">
              <div className="flex justify-between items-center">
                <h2 className={`text-2xl font-bold ${isUrdu ? 'font-urdu' : ''}`}>
                  {editingParty ? (isUrdu ? 'ترمیم' : 'Edit') : (isUrdu ? 'نیا شامل کریں' : 'Add New')}
                </h2>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingParty(null);
                  }}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-4">
                {/* Type */}
                <div>
                  <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                    {isUrdu ? 'قسم' : 'Type'}
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  >
                    <option value="supplier">{isUrdu ? 'سپلائر' : 'Supplier'}</option>
                    <option value="customer">{isUrdu ? 'کسٹمر' : 'Customer'}</option>
                  </select>
                </div>

                {/* Name */}
                <div>
                  <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                    {isUrdu ? 'نام (انگریزی)' : 'Name (English)'}
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>

                {/* Name Urdu */}
                <div>
                  <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                    {isUrdu ? 'نام (اردو)' : 'Name (Urdu)'}
                  </label>
                  <input
                    ref={formUrduNameInputRef}
                    type="text"
                    value={formData.nameUrdu}
                    onChange={(e) => setFormData({ ...formData, nameUrdu: e.target.value })}
                    onClick={handleFormUrduNameClick}
                    className={`w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 ${isUrdu ? 'font-urdu text-right' : ''}`}
                    style={isUrdu ? {
                      fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif",
                      lineHeight: '1.7',
                      paddingTop: '8px',
                      paddingBottom: '8px',
                      display: 'block',
                      overflow: 'visible'
                    } : {}}
                    dir="rtl"
                    lang="ur"
                    inputMode="text"
                    autoComplete="off"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                    {isUrdu ? 'فون نمبر' : 'Phone Number'}
                  </label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                {/* Address */}
                <div>
                  <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                    {isUrdu ? 'پتہ' : 'Address'}
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    rows="3"
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    dir={isUrdu ? 'rtl' : 'ltr'}
                  />
                </div>

                {/* Opening Balance */}
                <div>
                  <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                    {isUrdu ? 'ابتدائی بیلنس' : 'Opening Balance'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.openingBalance}
                    onChange={(e) => setFormData({ ...formData, openingBalance: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                {/* Balance Type */}
                <div>
                  <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                    {isUrdu ? 'بیلنس کی قسم' : 'Balance Type'}
                  </label>
                  <select
                    value={formData.balanceType}
                    onChange={(e) => setFormData({ ...formData, balanceType: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="receivable">{isUrdu ? 'قبض' : 'Receivable'}</option>
                    <option value="payable">{isUrdu ? 'ادائیگی' : 'Payable'}</option>
                  </select>
                </div>

                {/* Notes */}
                <div>
                  <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                    {isUrdu ? 'نوٹس' : 'Notes'}
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows="3"
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    dir={isUrdu ? 'rtl' : 'ltr'}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingParty(null);
                  }}
                  className={`px-6 py-2 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-all ${isUrdu ? 'font-urdu' : ''}`}
                >
                  {isUrdu ? 'منسوخ' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  className={`px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg font-semibold transition-all transform hover:scale-105 ${isUrdu ? 'font-urdu' : ''}`}
                >
                  {editingParty ? (isUrdu ? 'اپ ڈیٹ' : 'Update') : (isUrdu ? 'محفوظ' : 'Save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          .print\\:hidden {
            display: none !important;
          }
          .print\\:block {
            display: block !important;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          @page {
            margin: 0.5cm;
          }
          .bg-gradient-to-r {
            background: #059669 !important;
          }
          .from-green-600 {
            background: #059669 !important;
          }
          .to-emerald-600 {
            background: #059669 !important;
          }
          .bg-gradient-to-br {
            background: white !important;
          }
          .from-green-50 {
            background: white !important;
          }
          .via-emerald-50 {
            background: white !important;
          }
          .to-teal-50 {
            background: white !important;
          }
          .bg-white {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0.5cm !important;
          }
          .rounded-xl {
            border-radius: 0 !important;
          }
          .shadow-lg {
            box-shadow: none !important;
          }
          table {
            width: 100% !important;
            max-width: 100% !important;
            table-layout: auto !important;
          }
          .overflow-x-auto {
            width: 100% !important;
            overflow: visible !important;
          }
          div[ref] {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
        }
      `}</style>
      <UrduKeyboard
        isVisible={showUrduKeyboard}
        onKeyPress={handleSearchUrduKeyPress}
        onClose={() => setShowUrduKeyboard(false)}
      />
      <UrduKeyboard
        isVisible={showFormUrduKeyboard}
        onKeyPress={handleFormUrduKeyPress}
        onClose={() => setShowFormUrduKeyboard(false)}
      />
    </>
  );
}

export default UserManage;

